import { ShieldCheck, Clock, Users } from 'lucide-react';
import { useLocale } from '@/lib/i18n';
import { useSectionTranslation } from '@/hooks';
import {
  formatCount,
  formatMinutes,
  type MarketplaceStats,
} from '../useMarketplaceHome';
import { statsStripTranslations } from './translations';

interface StatsStripProps {
  stats: MarketplaceStats | null;
}

export function StatsStrip({ stats }: StatsStripProps) {
  const { locale } = useLocale();
  const t = useSectionTranslation(statsStripTranslations);

  if (!stats) return null;

  const items = [
    stats.ordersCompleted !== null && stats.ordersCompleted > 0
      ? { key: 'orders', icon: ShieldCheck, value: formatCount(stats.ordersCompleted, locale), label: t.ordersCompleted }
      : null,
    // `activeSellers` was in the payload and in the locale files but never
    // rendered, so the seller count was fetched on every page load and dropped.
    stats.activeSellers !== null && stats.activeSellers > 0
      ? { key: 'sellers', icon: Users, value: formatCount(stats.activeSellers, locale), label: t.activeSellers }
      : null,
    stats.avgDeliveryMinutes !== null && stats.avgDeliveryMinutes > 0
      ? { key: 'delivery', icon: Clock, value: formatMinutes(stats.avgDeliveryMinutes, t.minutes, locale), label: t.avgDelivery }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  if (items.length === 0) return null;

  return (
    <section className="border-y border-surface-border bg-surface-card/40" aria-label={t.ariaLabel}>
      <div className="container-main flex flex-wrap items-center justify-center sm:justify-between gap-x-10 gap-y-3 py-4">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.key} className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-card border border-surface-border text-accent">
                <Icon size={16} />
              </div>
              <div>
                <p className="text-lg font-semibold text-brand-white leading-none">{item.value}</p>
                <p className="text-xs text-brand-muted mt-0.5">{item.label}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
